import React, { useState } from 'react';
import {
   Box,
   ButtonBase,
   Collapse,
   Divider,
   ListItemButton,
   ListItemIcon,
   ListItemText,
   Menu,
   MenuItem,
   Typography,
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';

import { useApp } from '../../context/AppContext';
import {
   SUPPORTED_LANGUAGES,
   getLanguageByCode,
   DEFAULT_LANGUAGE,
} from '../../i18n/languages';
import {
   SUPPORTED_CURRENCIES,
   getCurrencyByCode,
   DEFAULT_CURRENCY,
} from '../../i18n/currencies';

// Letter code for a language (e.g. EN, FR). Deliberately not a flag: one
// language is spoken in many countries.
const LanguageBadge = ({ short }) => (
   <Box
      component="span"
      aria-hidden="true"
      sx={{
         display: 'inline-flex',
         alignItems: 'center',
         justifyContent: 'center',
         minWidth: 24,
         height: 18,
         px: 0.5,
         borderRadius: '4px',
         border: '1px solid currentColor',
         fontSize: 10,
         fontWeight: 700,
         letterSpacing: 0.3,
         lineHeight: 1,
         opacity: 0.85,
         whiteSpace: 'nowrap',
      }}
   >
      {short}
   </Box>
);

// Unified language + currency selector.
//   variant="drawer"    — collapsible section for mobile drawer menus
//   variant="menu"      — pill button + MUI Menu popup (default)
//   variant="menu-icon" — compact icon-only button
const LocaleSelector = ({
   variant = 'menu',
   onSelect,
   textColor,
   backgroundColor,
   hoverBackgroundColor,
}) => {
   const { t } = useTranslation();
   const { language, changeLanguage, currency, changeCurrency } = useApp();
   const [anchorEl, setAnchorEl] = useState(null);
   const [drawerOpen, setDrawerOpen] = useState(false);
   const [langSubOpen, setLangSubOpen] = useState(false);
   const [currencySubOpen, setCurrencySubOpen] = useState(false);

   const activeLangCode = language || DEFAULT_LANGUAGE;
   const activeLang = getLanguageByCode(activeLangCode) || SUPPORTED_LANGUAGES[0];
   const activeCurrCode = currency || DEFAULT_CURRENCY;
   const activeCurr = getCurrencyByCode(activeCurrCode) || SUPPORTED_CURRENCIES[0];

   const handleLangSelect = async (code) => {
      setAnchorEl(null);
      setDrawerOpen(false);
      setLangSubOpen(false);
      await changeLanguage(code);
      if (typeof onSelect === 'function') onSelect({ language: code });
   };

   const handleCurrencySelect = (code) => {
      setAnchorEl(null);
      setDrawerOpen(false);
      setCurrencySubOpen(false);
      changeCurrency(code);
      if (typeof onSelect === 'function') onSelect({ currency: code });
   };

   // ── Drawer variant ──
   if (variant === 'drawer') {
      return (
         <>
            <Divider sx={{ my: 0.5 }} />
            <ListItemButton
               onClick={(e) => {
                  e.stopPropagation();
                  setDrawerOpen((v) => !v);
               }}
               sx={{ py: 1.25 }}
            >
               <ListItemIcon sx={{ minWidth: 48, mr: 1 }}>
                  <LanguageBadge short={activeLang.short} />
               </ListItemIcon>
               <ListItemText
                  primary={t('nav.languageAndCurrency')}
                  secondary={`${activeLang.nativeLabel} · ${activeCurr.symbol} ${activeCurr.code}`}
                  primaryTypographyProps={{
                     variant: 'overline',
                     sx: { lineHeight: 1.2 },
                  }}
                  secondaryTypographyProps={{
                     sx: { fontSize: 14, fontWeight: 600, color: 'text.primary' },
                  }}
               />
               <KeyboardArrowDownRoundedIcon
                  sx={{
                     ml: 1,
                     opacity: 0.6,
                     transition: 'transform 0.2s',
                     transform: drawerOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
               />
            </ListItemButton>
            <Collapse in={drawerOpen} timeout="auto" unmountOnExit>
               {/* Language sub-section */}
               <ListItemButton
                  onClick={(e) => {
                     e.stopPropagation();
                     setLangSubOpen((v) => !v);
                  }}
                  sx={{ pl: 3, py: 0.75 }}
               >
                  <ListItemText
                     primary={t('nav.language')}
                     primaryTypographyProps={{
                        variant: 'overline',
                        sx: { fontSize: 11, fontWeight: 700, color: 'text.secondary' },
                     }}
                  />
                  <KeyboardArrowDownRoundedIcon
                     sx={{
                        fontSize: 16,
                        opacity: 0.5,
                        transition: 'transform 0.2s',
                        transform: langSubOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                     }}
                  />
               </ListItemButton>
               <Collapse in={langSubOpen} timeout="auto" unmountOnExit>
                  {SUPPORTED_LANGUAGES.map((lang) => (
                     <ListItemButton
                        key={lang.code}
                        selected={lang.code === activeLangCode}
                        onClick={(e) => {
                           e.stopPropagation();
                           handleLangSelect(lang.code);
                        }}
                        sx={{ pl: 5 }}
                     >
                        <ListItemIcon sx={{ minWidth: 48, mr: 1 }}>
                           <LanguageBadge short={lang.short} />
                        </ListItemIcon>
                        <ListItemText
                           primary={lang.nativeLabel}
                           secondary={lang.label !== lang.nativeLabel ? lang.label : undefined}
                        />
                        {lang.code === activeLangCode && (
                           <CheckIcon fontSize="small" sx={{ ml: 1, opacity: 0.7 }} />
                        )}
                     </ListItemButton>
                  ))}
               </Collapse>

               {/* Currency sub-section */}
               <ListItemButton
                  onClick={(e) => {
                     e.stopPropagation();
                     setCurrencySubOpen((v) => !v);
                  }}
                  sx={{ pl: 3, py: 0.75 }}
               >
                  <ListItemText
                     primary={t('nav.currency')}
                     primaryTypographyProps={{
                        variant: 'overline',
                        sx: { fontSize: 11, fontWeight: 700, color: 'text.secondary' },
                     }}
                  />
                  <KeyboardArrowDownRoundedIcon
                     sx={{
                        fontSize: 16,
                        opacity: 0.5,
                        transition: 'transform 0.2s',
                        transform: currencySubOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                     }}
                  />
               </ListItemButton>
               <Collapse in={currencySubOpen} timeout="auto" unmountOnExit>
                  {SUPPORTED_CURRENCIES.map((cur) => (
                     <ListItemButton
                        key={cur.code}
                        selected={cur.code === activeCurrCode}
                        onClick={(e) => {
                           e.stopPropagation();
                           handleCurrencySelect(cur.code);
                        }}
                        sx={{ pl: 5, py: 0.5 }}
                     >
                        <ListItemText
                           primary={`${cur.symbol} ${cur.code}`}
                           secondary={cur.label}
                           primaryTypographyProps={{ sx: { fontSize: 14, fontWeight: 500 } }}
                           secondaryTypographyProps={{ sx: { fontSize: 12 } }}
                        />
                        {cur.code === activeCurrCode && (
                           <CheckIcon fontSize="small" sx={{ ml: 1, opacity: 0.7 }} />
                        )}
                     </ListItemButton>
                  ))}
               </Collapse>
            </Collapse>
            <Divider sx={{ my: 0.5 }} />
         </>
      );
   }

   // ── Menu / menu-icon variants ──
   const open = Boolean(anchorEl);
   const iconOnly = variant === 'menu-icon';
   const resolvedTextColor = textColor || 'inherit';
   const resolvedBg = backgroundColor || (iconOnly ? 'rgba(0,0,0,0.04)' : 'transparent');
   const resolvedHoverBg = hoverBackgroundColor || (iconOnly ? 'rgba(0,0,0,0.08)' : 'rgba(128,128,128,0.1)');

   return (
      <>
         <ButtonBase
            onClick={(e) => setAnchorEl(e.currentTarget)}
            aria-label={t('nav.languageAndCurrency')}
            aria-haspopup="listbox"
            aria-expanded={open ? 'true' : 'false'}
            disableRipple
            sx={{
               display: 'inline-flex',
               alignItems: 'center',
               justifyContent: 'center',
               gap: iconOnly ? 0 : 0.5,
               height: 36,
               minWidth: iconOnly ? 36 : 0,
               px: iconOnly ? 0 : 1.25,
               borderRadius: iconOnly ? '50%' : '18px',
               border: iconOnly ? 'none' : '1px solid',
               borderColor: textColor ? `${textColor}33` : 'rgba(128,128,128,0.3)',
               color: resolvedTextColor,
               backgroundColor: resolvedBg,
               transition: 'background-color 0.2s, border-color 0.2s',
               '&:hover': { backgroundColor: resolvedHoverBg },
               '&:focus-visible': {
                  outline: '2px solid rgba(59,85,243,0.5)',
                  outlineOffset: 2,
               },
            }}
         >
            <LanguageBadge short={activeLang.short} />
            {!iconOnly && (
               <>
                  <Box
                     component="span"
                     sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        letterSpacing: 0.4,
                        lineHeight: 1,
                     }}
                  >
                     {activeCurr.symbol}
                  </Box>
                  <KeyboardArrowDownRoundedIcon
                     sx={{
                        fontSize: 16,
                        opacity: 0.6,
                        transition: 'transform 0.2s',
                        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                     }}
                  />
               </>
            )}
         </ButtonBase>
         <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{ zIndex: 9999 }}
            PaperProps={{
               elevation: 3,
               sx: {
                  mt: 0.5,
                  minWidth: 200,
                  maxHeight: 420,
                  borderRadius: 2,
                  overflow: 'auto',
               },
            }}
            MenuListProps={{ sx: { py: 0.5 } }}
         >
            {/* Language section header */}
            <Typography
               variant="overline"
               sx={{
                  px: 1.5,
                  pt: 0.75,
                  pb: 0.25,
                  display: 'block',
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'text.secondary',
                  letterSpacing: 1,
               }}
            >
               {t('nav.language')}
            </Typography>
            {SUPPORTED_LANGUAGES.map((lang) => (
               <MenuItem
                  key={lang.code}
                  selected={lang.code === activeLangCode}
                  onClick={() => handleLangSelect(lang.code)}
                  sx={{
                     py: 0.75,
                     px: 1.5,
                     gap: 1,
                     '&.Mui-selected': {
                        backgroundColor: 'rgba(59,85,243,0.08)',
                     },
                     '&.Mui-selected:hover': {
                        backgroundColor: 'rgba(59,85,243,0.12)',
                     },
                  }}
               >
                  <LanguageBadge short={lang.short} />
                  <Box
                     component="span"
                     sx={{
                        flex: 1,
                        fontSize: 14,
                        fontWeight: lang.code === activeLangCode ? 600 : 500,
                     }}
                  >
                     {lang.nativeLabel}
                  </Box>
                  {lang.code === activeLangCode && (
                     <CheckIcon
                        fontSize="small"
                        sx={{ ml: 1, color: 'rgba(59,85,243,0.9)' }}
                     />
                  )}
               </MenuItem>
            ))}

            <Divider sx={{ my: 0.5 }} />

            {/* Currency section header */}
            <Typography
               variant="overline"
               sx={{
                  px: 1.5,
                  pt: 0.5,
                  pb: 0.25,
                  display: 'block',
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'text.secondary',
                  letterSpacing: 1,
               }}
            >
               {t('nav.currency')}
            </Typography>
            {SUPPORTED_CURRENCIES.map((cur) => (
               <MenuItem
                  key={cur.code}
                  selected={cur.code === activeCurrCode}
                  onClick={() => handleCurrencySelect(cur.code)}
                  sx={{
                     py: 0.5,
                     px: 1.5,
                     gap: 0.75,
                     '&.Mui-selected': {
                        backgroundColor: 'rgba(59,85,243,0.08)',
                     },
                     '&.Mui-selected:hover': {
                        backgroundColor: 'rgba(59,85,243,0.12)',
                     },
                  }}
               >
                  <Box
                     component="span"
                     sx={{
                        fontSize: 13,
                        fontWeight: cur.code === activeCurrCode ? 600 : 500,
                        minWidth: 32,
                     }}
                  >
                     {cur.symbol}
                  </Box>
                  <Box
                     component="span"
                     sx={{
                        flex: 1,
                        fontSize: 13,
                        fontWeight: cur.code === activeCurrCode ? 600 : 400,
                     }}
                  >
                     {cur.code}
                  </Box>
                  <Box
                     component="span"
                     sx={{ fontSize: 11, color: 'text.secondary' }}
                  >
                     {cur.label}
                  </Box>
                  {cur.code === activeCurrCode && (
                     <CheckIcon
                        fontSize="small"
                        sx={{ ml: 0.5, color: 'rgba(59,85,243,0.9)', fontSize: 16 }}
                     />
                  )}
               </MenuItem>
            ))}
         </Menu>
      </>
   );
};

export default LocaleSelector;
